import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
  doc,
  onSnapshot,
  collection,
  query,
  where,
  onSnapshot as onColSnapshot,
  runTransaction,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { toast } from "react-hot-toast";
import { ArrowLeft, Clock, Share2 } from "lucide-react";
import { countryToDial, toE164 } from "../../utils/phone";

/* helpers */
function symbolOf(code) {
  switch (code) {
    case "USD":
      return "$";
    case "EUR":
      return "€";
    case "GBP":
      return "£";
    case "INR":
      return "₹";
    case "LKR":
      return "Rs";
    default:
      return code || "$";
  }
}

// parse "10, 23  33" -> [10,23,33] (unique, 0..99)
function parseLuckyCsv(csv = "") {
  const out = [];
  const seen = new Set();
  csv
    .split(/[,\s]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((s) => {
      const n = Number(s);
      if (Number.isInteger(n) && n >= 0 && n <= 99 && !seen.has(n)) {
        seen.add(n);
        out.push(n);
      }
    });
  return out;
}

function Grid({ card }) {
  const filled = new Set(card?.filled || []);
  const locked = new Set(card?.locked || []);
  const all = [...Array(100).keys()];
  const cellClass = (n) =>
    filled.has(n)
      ? "bg-lime-400 text-black font-bold"
      : locked.has(n)
      ? "bg-amber-400 text-black"
      : "bg-slate-700 text-white/90";
  return (
    <div className="grid grid-cols-10 gap-1 mt-3">
      {all.map((n) => (
        <div
          key={n}
          className={`h-7 rounded text-[11px] leading-7 text-center ${cellClass(
            n
          )}`}
        >
          {n.toString().padStart(2, "0")}
        </div>
      ))}
    </div>
  );
}

function DirectAllocationPanel({ card, agentUid }) {
  const [mobile, setMobile] = useState("");
  const [numbersCsv, setNumbersCsv] = useState("");
  const [busy, setBusy] = useState(false);

  const unit = Number(card?.pricePerNumber || 0);
  const picked = useMemo(() => parseLuckyCsv(numbersCsv), [numbersCsv]);
  const amount = unit * picked.length || 0;

  const allocateNow = async () => {
    if (!card?.id) return toast.error("Card not loaded.");
    if (!agentUid) return toast.error("Agent not found.");
    const lucky = parseLuckyCsv(numbersCsv);
    if (!lucky.length) return toast.error("Enter at least one number (0–99).");

    setBusy(true);
    try {
      await runTransaction(db, async (tx) => {
        const cardRef = doc(db, "cards", card.id);
        const cardSnap = await tx.get(cardRef);
        if (!cardSnap.exists()) throw new Error("Card not found.");
        const c = cardSnap.data();

        // filter to available numbers
        const filled = new Set(c.filled || []);
        const locked = new Set(c.locked || []);
        const available = lucky.filter((n) => !filled.has(n) && !locked.has(n));
        if (!available.length) throw new Error("All chosen numbers are taken.");

        // normalize mobile (E.164) – use agent country on card if present
        const defaultDial =
          countryToDial(c.agentCountryCode || c.agentCountry) || "+94";
        const e164 = toE164((mobile || "").trim(), defaultDial);

        // create ticket
        const ticketRef = doc(collection(db, "tickets"));
        const unitPrice = Number(c.pricePerNumber || 0);
        const ticketAmount = Number((unitPrice * available.length).toFixed(2));
        const bundleId = `${agentUid}|${e164 || "direct"}`;

        tx.set(ticketRef, {
          id: ticketRef.id,
          status: "payment_approved", // direct-sold
          allocated: true,
          allocatedBy: agentUid,
          allocatedAt: serverTimestamp(),
          paymentApprovedAt: serverTimestamp(),

          cardId: card.id,
          agentUid,
          agentName:
            c.agentName || (c.agentUid ? c.agentUid.slice(0, 6) : "Agent"),

          userUid: null,
          userName: e164 ? `Mobile ${String(e164).slice(-4)}` : "Direct Buyer",
          userMobileE164: e164 || "",
          userMobile: e164 || "",

          numbers: available,
          currency: c.currency || "USD",
          unitPrice,
          amount: ticketAmount,
          bundleId,
          updatedAt: serverTimestamp(),
        });

        // update card: move numbers to filled, remove from locked, bump sales
        tx.update(cardRef, {
          filled: arrayUnion(...available),
          locked: arrayRemove(...available),
          todaySales: Number((c.todaySales || 0) + ticketAmount),
        });
      });

      toast.success("Allocated & marked paid.");
      setNumbersCsv("");
      setMobile("");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Direct allocation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 p-4 rounded-2xl bg-slate-800 ring-1 ring-slate-700">
      <div className="flex items-center justify-between mb-2">
        <div className="font-semibold">Direct Allocation</div>
        <div className="text-xs text-slate-400">
          Unit: {symbolOf(card.currency)}
          {unit.toFixed(2)}
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_1fr_auto] gap-2">
        <input
          className="px-3 py-2 rounded bg-slate-900 text-white ring-1 ring-slate-700"
          placeholder="Lucky numbers (e.g. 10, 23, 33)"
          value={numbersCsv}
          onChange={(e) => setNumbersCsv(e.target.value)}
          disabled={busy}
        />
        <input
          className="px-3 py-2 rounded bg-slate-900 text-white ring-1 ring-slate-700"
          placeholder="Mobile (e.g. +9476… or 076…)"
          value={mobile}
          onChange={(e) => setMobile(e.target.value)}
          disabled={busy}
        />
        <button
          onClick={allocateNow}
          disabled={busy || !picked.length}
          className="px-3 py-2 rounded-xl bg-lime-400 text-black font-semibold disabled:opacity-50"
        >
          {busy ? "Allocating…" : "Allocate & Mark Paid"}
        </button>
      </div>

      <div className="mt-2 text-xs text-slate-300">
        Preview: <b>{picked.length}</b> numbers ·{" "}
        <b>
          {symbolOf(card.currency)}
          {amount.toFixed(2)}
        </b>
      </div>
    </div>
  );
}

export default function AgentCardDetail({ user }) {
  const navigate = useNavigate();
  const { cardId: raw } = useParams();
  const cardId = useMemo(() => {
    try {
      return decodeURIComponent(raw || "");
    } catch {
      return raw || "";
    }
  }, [raw]);

  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reqCount, setReqCount] = useState(0);
  const [payCount, setPayCount] = useState(0);

  useEffect(() => {
    if (!raw) {
      toast.error("No card id");
      navigate("/agent", { replace: true });
    }
  }, [raw, navigate]);

  useEffect(() => {
    if (!cardId) return;
    setLoading(true);
    const ref = doc(db, "cards", cardId);
    const un = onSnapshot(
      ref,
      (snap) => {
        setCard(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        toast.error(`Card stream error: ${err.message}`);
        setLoading(false);
      }
    );
    return () => un();
  }, [cardId]);

  useEffect(() => {
    if (!user?.uid || !cardId) return;
    const qReq = query(
      collection(db, "tickets"),
      where("agentUid", "==", user.uid),
      where("cardId", "==", cardId),
      where("status", "==", "requested")
    );
    const qPay = query(
      collection(db, "tickets"),
      where("agentUid", "==", user.uid),
      where("cardId", "==", cardId),
      where("status", "==", "payment_submitted")
    );
    const un1 = onColSnapshot(qReq, (s) => setReqCount(s.size));
    const un2 = onColSnapshot(qPay, (s) => setPayCount(s.size));
    return () => {
      un1();
      un2();
    };
  }, [user?.uid, cardId]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto mt-8 md:mt-16 p-4">
        <div className="animate-pulse h-8 w-40 bg-slate-700 rounded mb-4" />
        <div className="animate-pulse h-40 bg-slate-800 rounded-2xl" />
      </div>
    );
  }

  if (!card) {
    return (
      <div className="max-w-5xl mx-auto mt-8 md:mt-16 p-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-800 ring-1 ring-slate-700 hover:bg-slate-700"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="mt-6 p-6 rounded-2xl bg-slate-800 ring-1 ring-slate-700">
          <div className="text-lg font-semibold">Card not found</div>
          <div className="text-sm text-slate-300 mt-1">
            Make sure the URL id is correct. (Tried: <code>{cardId}</code>)
          </div>
        </div>
      </div>
    );
  }

  const drawStr = card.drawAt
    ? new Date(card.drawAt?.toDate?.() || card.drawAt).toLocaleString()
    : "—";

  const sold = (card.filled || []).length;
  const lockedOnly = (card.locked || []).filter(
    (n) => !(card.filled || []).includes(n)
  ).length;
  const unit = Number(card.pricePerNumber || 0);
  const currency = symbolOf(card.currency);

  return (
    <div className="max-w-5xl mx-auto mt-8 md:mt-16 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-800 ring-1 ring-slate-700 hover:bg-slate-700"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <div className="text-xl font-bold">
              Card #{card.id.slice(0, 8)}…
            </div>
            <div className="text-xs text-slate-300 flex items-center gap-2">
              <Clock size={14} />
              <span>Draw {drawStr}</span>
              <span className="mx-1">•</span>
              <span>
                {currency}
                {unit.toFixed(2)}/num
              </span>
              <span className="mx-1">•</span>
              <span>Payout {card.payoutPct ?? 80}%</span>
              <span className="mx-1">•</span>
              <span className="capitalize">{card.status || "active"}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Link
            to="/agent"
            className="px-3 py-1.5 rounded-xl bg-slate-800 ring-1 ring-slate-700 hover:bg-slate-700 text-sm"
          >
            Dashboard
          </Link>
          <button
            onClick={() => {
              const url = `${
                window.location.origin
              }/lottery?agent=${encodeURIComponent(
                card.agentUid
              )}&card=${encodeURIComponent(card.id)}`;
              navigator.clipboard.writeText(url);
              toast.success("Share link copied");
            }}
            className="px-3 py-1.5 rounded-xl bg-slate-700 hover:bg-slate-600 text-sm inline-flex items-center gap-1"
          >
            <Share2 size={16} /> Share
          </button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700">
          <div className="text-xs text-slate-400">Sold</div>
          <div className="text-xl font-bold">{sold}/100</div>
        </div>
        <div className="p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700">
          <div className="text-xs text-slate-400">On Hold</div>
          <div className="text-xl font-bold">{lockedOnly}</div>
        </div>
        <div className="p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700">
          <div className="text-xs text-slate-400">Pending Requests</div>
          <div className="text-xl font-bold">{reqCount}</div>
        </div>
        <div className="p-3 rounded-xl bg-slate-800 ring-1 ring-slate-700">
          <div className="text-xs text-slate-400">Payments to Verify</div>
          <div className="text-xl font-bold">{payCount}</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-300">
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded bg-lime-400" /> Sold
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded bg-amber-400" /> On Hold
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-block w-3 h-3 rounded bg-slate-700" />{" "}
          Available
        </span>
      </div>

      {/* Direct allocation */}
      <DirectAllocationPanel card={card} agentUid={user?.uid} />

      <div className="p-4 mt-3 rounded-2xl bg-slate-800 ring-1 ring-slate-700">
        <Grid card={card} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          onClick={() =>
            navigate(`/agent/queues/${encodeURIComponent(card.id)}`)
          }
          className="px-3 py-2 rounded-xl bg-lime-400 text-black font-semibold hover:brightness-95"
        >
          Open Queues for this Card
        </button>
        <button
          onClick={() => toast("Close Card: coming soon")}
          className="px-3 py-2 rounded-xl bg-slate-700 text-white"
        >
          Close Card
        </button>
      </div>
    </div>
  );
}
